import { Component, EventEmitter, OnInit, Output, OnDestroy, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { CreditDetailsService } from './credit-details.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-credit-details',
    templateUrl: './credit-details.component.html',
    styleUrls: ['./credit-details.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CreditDetailsComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;

  // Component properties from AngularJS
  modalTitle = 'Credit Details Adjustment';
  modalData: any[] = [];
  disable = true;
  loading = false;
  crudLoading = false;
  showFormLoader = true;

  // Entity data properties
  entityList: any[] = [];
  leId: string = '';
  cdr_no: string = '';
  leType: string = '';

  // Global data properties
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  // User settings
  userSettings: any;
  logged_in_user: string = '';

  constructor(
    private creditDetailsService: CreditDetailsService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.init();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.SERVER_CONFIG = this.gtwApp.SERVER_CONFIG || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? extractObject(rawRow[0]) : extractObject(rawRow);
    this.cdr.markForCheck();
  }

  init() {
    try {
      if (!this.globalService || !this.GENERAL_CONFIG || !Object.keys(this.GENERAL_CONFIG).length || !this.resolveApiBase()) {
        return;
      }

      this.userSettings = this.USER_SETTINGS;
      this.logged_in_user = this.userSettings?.user?.sso_id || '';

      if (this.rowData && Object.keys(this.rowData).length > 0 && this.filterParams && this.filterParams.filing_key && this.filterParams.ho_le_key) {
        this.setupEntityData();
        this.getModalData();
      }
    } catch (e) {
      console.error('Error in CreditDetails init:', e);
    }
  }

  private setupEntityData() {
    // 🔴 CRITICAL: Use REAL data from rowData - NO TEST_ values!
    this.leId = this.rowData.LEID;
    this.cdr_no = this.rowData.MARS;
    this.leType = this.rowData.LE_TYPE;

    // 🔴 MANDATORY ORDER: Tax Year, LEID, LE Name
    this.entityList = [
      {key: "Tax Year", value: this.rowData.TAX_YEAR},
      {key: "LEID", value: this.rowData.LEID},
      {key: "LE Name", value: this.rowData.LE_NAME}
    ];
  }

  getModalData() {
    this.loading = true;
    this.showFormLoader = true;

    this.creditDetailsService.getModalData(
      this.resolveApiBase(),
      this.globalService,
      this.filterParams,
      this.rowData,
      this.logged_in_user
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.modalData = response.jsonObject;
        if (this.modalData && this.modalData.length > 0) {
          this.modalData = this.modalData.map(x => ({
            ...x,
            totalCYAmount: Number(x.GENERATED_AMT) + Number(x.ADJUSTMENT_AMT)
          }));
          this.modalData.sort((a, b) => a.LINE_ORDER - b.LINE_ORDER);
        } else {
          this._snackBar.open('No data found!', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        }
        this.loading = false;
        this.showFormLoader = false;
      },
      (error: any) => {
        this.loading = false;
        this.showFormLoader = false;
        this._snackBar.open('Error loading data. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  valueChanged(item: any, index: number) {
    item.IS_CHANGED = 'Y';

    // Remove commas before calculation
    const generatedAmt = parseFloat(this.removeCommas(item.GENERATED_AMT)) || 0;
    const adjustmentAmt = parseFloat(this.removeCommas(item.ADJUSTMENT_AMT)) || 0;

    // Calculate total and store as clean number
    item.totalCYAmount = generatedAmt + adjustmentAmt;

    this.disable = false;
  }

  save() {
    this.isSaveClicked = true;
    this.crudLoading = true;

    const clob_settings = {
      tax_year: this.filterParams.tax_year,
      scenario: this.filterParams.scenario,
      jcd_key: this.rowData.jcd_key ? this.rowData.jcd_key : "250",
      ta_key: this.modalData[0].TA_KEY + "",
      ta_mast_key: this.modalData[0].TA_MAST_KEY + "",
      consol_group_key: this.modalData[0].CONSOL_GROUP_KEY + "",
      sso_id: this.logged_in_user,
      screen_key: this.globalService?.screen_key
    };

    this.creditDetailsService.saveModal(
      this.resolveApiBase(),
      this.modalData,
      clob_settings,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Form Details are successfully updated.', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  reset() {
    this.getModalData();
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  // Track by function for ngFor
  itemTracker(index: number, item: any): any {
    return item ? item.LINE_NO || index : index;
  }

  // Number formatting functions (from consolidation-ledgers pattern)
  formatNumber(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    // Convert to string and remove any existing commas
    let numStr = value.toString().replace(/,/g, '');

    // Handle negative numbers
    const isNegative = numStr.startsWith('-');
    if (isNegative) {
      numStr = numStr.substring(1);
    }

    // Add commas for thousands
    const formatted = numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return isNegative ? '-' + formatted : formatted;
  }

  removeCommas(value: string): string {
    if (!value) return '';
    return value.toString().replace(/,/g, '');
  }

  private resolveApiBase(): string {
    return this.GENERAL_CONFIG?.base_url || '';
  }

  // Handle input formatting with commas for amount fields
  onAmountInput(event: any, item: any, field: string) {
    const input = event.target;
    const value = input.value;

    // Remove commas and non-numeric characters except minus sign
    const cleanValue = value.replace(/[^0-9.-]/g, '');

    // Update the item field with clean value
    item[field] = cleanValue;

    // Format the display value with commas
    if (cleanValue !== '' && !isNaN(parseFloat(cleanValue))) {
      input.value = this.formatNumber(cleanValue);
    } else {
      input.value = cleanValue;
    }

    // Call the original valueChanged method
    this.valueChanged(item, 0);
  }
}
